/** Довідкові дані організації: сесія, люди, підрозділи, звання, посадовці. */

import {
  can,
  type Bootstrap,
  type Capability,
  type OrgSummary,
  type DocTemplate,
  type Official,
  type OrgSettings,
  type Person,
  type Position,
  type Rank,
  type SessionInfo,
  type Unit,
} from '@kancelaria/shared';
import { create } from 'zustand';
import { api, ApiFailure } from '../api/client.js';
import { useLink } from './link.js';
import { useMarks } from './marks.js';

type Status = 'loading' | 'anon' | 'ready' | 'down';

interface DataState {
  status: Status;
  session: SessionInfo | null;
  /** Курси, доступні користувачу; власнику — усі. */
  orgs: OrgSummary[];
  people: Person[];
  units: Unit[];
  ranks: Rank[];
  positions: Position[];
  officials: Official[];
  templates: DocTemplate[];
  rev: number;
  load: () => Promise<void>;
  login: (login: string, password: string, org?: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Перейти в інший курс (доступно власнику). */
  switchOrg: (orgId: string) => Promise<void>;
  patchSettings: (settings: OrgSettings) => void;
  patchPerson: (id: string, patch: Partial<Person>) => void;
  removePersonLocal: (id: string) => void;
}

const empty = {
  session: null,
  orgs: [],
  people: [],
  units: [],
  ranks: [],
  positions: [],
  officials: [],
  templates: [],
  rev: 0,
};

const BOOT_KEY = 'kanc.bootstrap';

function readBoot(): Bootstrap | null {
  try {
    const raw = localStorage.getItem(BOOT_KEY);
    if (!raw) return null;
    const b = JSON.parse(raw) as Bootstrap;
    return b?.session ? b : null;
  } catch {
    return null;
  }
}

function writeBoot(b: Bootstrap): void {
  try {
    localStorage.setItem(BOOT_KEY, JSON.stringify(b));
  } catch {
    /* квота */
  }
}

function clearBoot(): void {
  try {
    localStorage.removeItem(BOOT_KEY);
  } catch {
    /* */
  }
}

function persistNow(): void {
  const s = useData.getState();
  if (!s.session) return;
  writeBoot({
    session: s.session,
    orgs: s.orgs,
    people: s.people,
    units: s.units,
    ranks: s.ranks,
    positions: s.positions,
    officials: s.officials,
    templates: s.templates,
    rev: s.rev,
    serverTime: new Date().toISOString(),
  });
}

export const useData = create<DataState>((set, get) => ({
  status: 'loading',
  ...empty,

  load: async () => {
    try {
      const b = await api.get<Bootstrap>('/api/bootstrap');
      writeBoot(b);
      set({ status: 'ready', ...b, session: b.session });
      useLink.getState().setOnline(true);
    } catch (e) {
      if (e instanceof ApiFailure && e.status === 401) {
        clearBoot();
        set({ status: 'anon', ...empty });
        return;
      }
      const cached = readBoot();
      if (cached) {
        set({ status: 'ready', ...cached, session: cached.session });
        useLink.getState().setOnline(false);
        return;
      }
      set({ status: 'down', ...empty });
      useLink.getState().setOnline(false);
    }
  },

  login: async (login, password, org) => {
    await api.post<SessionInfo>('/api/auth/login', { login, password, org });
    await get().load();
  },

  logout: async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      /* вихід локальний навіть без сервера */
    }
    clearBoot();
    set({ status: 'anon', ...empty });
  },

  refresh: async () => {
    try {
      const b = await api.get<Bootstrap>('/api/bootstrap');
      writeBoot(b);
      set({ ...b, session: b.session, status: 'ready' });
    } catch {
      useLink.getState().setOnline(false);
    }
  },

  switchOrg: async orgId => {
    // Незаписані позначки належать старому курсу: відправляємо їх до
    // перемикання, інакше сервер прийняв би їх уже в новому.
    await useMarks.getState().flush();
    await api.post('/api/orgs/switch', { orgId });
    await get().refresh();
  },

  patchSettings: settings => {
    const s = get().session;
    if (!s) return;
    set({ session: { ...s, org: { ...s.org, settings } } });
    persistNow();
  },

  patchPerson: (id, patch) => {
    set(s => ({ people: s.people.map(p => (p.id === id ? { ...p, ...patch } : p)) }));
    persistNow();
  },

  removePersonLocal: id => {
    set(s => ({ people: s.people.filter(p => p.id !== id) }));
    persistNow();
  },
}));

/* ------------------------------------------------------------- селектори */

export const useCan = (cap: Capability): boolean =>
  useData(s => (s.session ? can(s.session.user, cap) : false));

export const unitName = (units: Unit[], id: string | null): string =>
  units.find(u => u.id === id)?.name ?? '';
